import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CreditCard,
  Truck,
  Shield,
  ArrowLeft,
  Check,
  Mail,
  MapPin,
  Eye,
  EyeOff,
  Lock,
  User,
  Loader2,
  Store,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import AddressSelectorModal from "@/components/dashboard/addresses/address-selector";
import PaystackLogo from "@/assets/images/logo/paystack-logo.png";
import FlutterwaveLogo from "@/assets/images/logo/flutterwave-logo.png";
import { useAuth } from "@/context/Auth";
import axios from "@/utils/axios";
import Seo from "@/seo/seo";
import { routeSeoConfig } from "@/utils/seo-config";
import CheckoutSkeleton from "./checkout-skeleton";
import { toast } from "sonner";
import { registerUser } from "@/utils/helper/auth";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  loadCart,
  syncCartToServer,
  syncFromLocalStorage,
  fetchCartTotals,
  selectCartItems,
  selectCartLoading,
  selectCartSubtotal,
  selectCartTotal,
  selectCartTotals,
  selectCartTotalsLoading,
  selectCartCoupon,
} from "@/store/slices/cartSlice";
import {
  initiatePayment,
  setSelectedGateway,
  fetchPaymentGatewaysStatus,
  selectInitiatingPayment,
  selectCurrentPayment,
  selectSelectedGateway,
  selectPaystackEnabled,
  selectFlutterwaveEnabled,
  selectGatewayStatusesLoading,
} from "@/store/slices/paymentSlice";
import { createOrder } from "@/store/slices/orderSlice";
import {
  calculateShippingCost,
  getShippingMethodDetails,
  getEstimatedDelivery,
} from "@/utils/shippingCalculator";

export default function CheckoutPage() {
  const { user, setUser, loading: authLoading, fetchUser } = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux selectors
  const cartItems = useAppSelector(selectCartItems);
  const loading = useAppSelector(selectCartLoading);
  const subtotal = useAppSelector(selectCartSubtotal);
  const total = useAppSelector(selectCartTotal);
  const cartTotals = useAppSelector(selectCartTotals);
  const cartTotalsLoading = useAppSelector(selectCartTotalsLoading);
  const coupon = useAppSelector(selectCartCoupon);
  const [isAddressSelectorOpen, setIsAddressSelectorOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  // Payment Redux state
  const selectedPaymentMethod = useAppSelector(selectSelectedGateway);
  const initiatingPayment = useAppSelector(selectInitiatingPayment);
  const currentPayment = useAppSelector(selectCurrentPayment);
  const paystackEnabled = useAppSelector(selectPaystackEnabled);
  const flutterwaveEnabled = useAppSelector(selectFlutterwaveEnabled);
  const gatewayStatusesLoading = useAppSelector(selectGatewayStatusesLoading);
  const [shippingAddress, setShippingAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  // Pickup configuration
  const [deliveryMethod, setDeliveryMethod] = useState("delivery"); // 'delivery' or 'pickup'
  const PICKUP_ADDRESS = {
    address: "FCDA Quarters, Kashim Ibrahim Way, Wuse 2",
    city: "Abuja",
    state: "FCT",
    country: "Nigeria",
    zipCode: "900288",
  };

  // Login form state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  // Register form state
  const [registerFormData, setRegisterFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);

  // ✅ Check if user is authenticated on mount
  useEffect(() => {
    fetchUser();
  }, []);

  // ✅ Re-check auth after login modal closes (in case user logged in)
  useEffect(() => {
    if (!isLoginModalOpen && !isRegisterModalOpen) {
      fetchUser();
    }
  }, [isLoginModalOpen, isRegisterModalOpen]);

  // ✅ Redirect to cart if not authenticated (after auth check completes)
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please log in to proceed to checkout");
      navigate("/cart");
    }
  }, [authLoading, user, navigate]);

  // ✅ Auto-fill email from user token when user is authenticated
  useEffect(() => {
    if (user && user.email) {
      setShippingAddress((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [user]);

  // ✅ Load cart items
  useEffect(() => {
    // For guests, sync from localStorage first to ensure we have the latest data
    if (!user) {
      dispatch(syncFromLocalStorage());
    }
    // Then load cart (from server for logged in users, or from localStorage for guests)
    dispatch(loadCart(user));
  }, [dispatch, user]);

  // ✅ Fetch payment gateway statuses on mount
  useEffect(() => {
    dispatch(fetchPaymentGatewaysStatus());
  }, [dispatch]);

  // ✅ Auto-select first enabled gateway if current selection is disabled
  useEffect(() => {
    if (!gatewayStatusesLoading) {
      if (!selectedPaymentMethod) {
        if (paystackEnabled) {
          dispatch(setSelectedGateway("paystack"));
        } else if (flutterwaveEnabled) {
          dispatch(setSelectedGateway("flutterwave"));
        }
      }

      if (selectedPaymentMethod === "paystack" && !paystackEnabled) {
        // Paystack is disabled, switch to Flutterwave if enabled
        if (flutterwaveEnabled) {
          dispatch(setSelectedGateway("flutterwave"));
        }
      } else if (
        selectedPaymentMethod === "flutterwave" &&
        !flutterwaveEnabled
      ) {
        // Flutterwave is disabled, switch to Paystack if enabled
        if (paystackEnabled) {
          dispatch(setSelectedGateway("paystack"));
        }
      } else if (!paystackEnabled && !flutterwaveEnabled) {
        // Both are disabled - this shouldn't happen, but handle gracefully
      }
    }
  }, [
    gatewayStatusesLoading,
    paystackEnabled,
    flutterwaveEnabled,
    selectedPaymentMethod,
    dispatch,
  ]);

  // ✅ Redirect to cart if cart is empty
  useEffect(() => {
    if (!loading && cartItems.length === 0) {
      navigate("/cart");
    }
  }, [cartItems.length, loading, navigate]);

  const formatNaira = (amount) =>
    new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount);

  // Dynamic shipping calculation
  const [shippingCost, setShippingCost] = useState(null); // null when no state selected

  // Calculate shipping cost dynamically when state or cart items change
  useEffect(() => {
    if (
      cartItems.length > 0 &&
      shippingAddress.state &&
      shippingAddress.state.trim()
    ) {
      const totalWeight = cartItems.reduce((sum, item) => {
        const weight = item.weight || 0.5; // Default 0.5kg if no weight
        return sum + weight * (item.quantity || 1);
      }, 0);

      const method = getShippingMethodDetails(totalWeight);
      const cost = calculateShippingCost(
        cartItems,
        shippingAddress.state,
        method.code,
      );
      setShippingCost(cost);
    } else {
      // No shipping cost when no state selected
      setShippingCost(null);
    }
  }, [cartItems, shippingAddress.state]);

  // Handle shipping cost when delivery method changes
  useEffect(() => {
    if (deliveryMethod === "pickup") {
      setShippingCost(0);
    } else {
      // Re-trigger calculation logic if switching back to delivery
      if (
        cartItems.length > 0 &&
        shippingAddress.state &&
        shippingAddress.state.trim()
      ) {
        const totalWeight = cartItems.reduce((sum, item) => {
          const weight = item.weight || 0.5;
          return sum + weight * (item.quantity || 1);
        }, 0);

        const method = getShippingMethodDetails(totalWeight);
        const cost = calculateShippingCost(
          cartItems,
          shippingAddress.state,
          method.code,
        );
        setShippingCost(cost);
      } else {
        setShippingCost(null);
      }
    }
  }, [deliveryMethod, cartItems, shippingAddress.state]);

  // Fetch cart totals from backend (includes tax calculated from system settings)
  useEffect(() => {
    if (user && cartItems.length > 0 && shippingCost !== null) {
      const discountAmount = coupon?.discountAmount || 0;
      // Ensure shippingCost is a number
      const shippingCostNum = Number(shippingCost) || 0;
      dispatch(
        fetchCartTotals({ shippingCost: shippingCostNum, discountAmount }),
      );
    }
  }, [dispatch, user, cartItems.length, shippingCost, coupon]);

  // Use backend totals if available, otherwise fallback to local calculation
  const displayTax = cartTotals?.taxAmount ?? subtotal * 0.08;
  const displayTotal = cartTotals?.total ?? total;

  const handleSelectAddress = (address) => {
    setShippingAddress({
      firstName: address.name.split(" ")[0] || "",
      lastName: address.name.split(" ")[1] || "",
      address: address.address,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country,
    });
  };

  const handlePaymentMethodChange = (value) => {
    dispatch(setSelectedGateway(value));
  };

  // Handle payment initiation and redirect
  useEffect(() => {
    if (currentPayment) {
      // Check for authorization URL in various possible fields
      const authorizationUrl =
        currentPayment.authorizationUrl ||
        currentPayment.authorization_url ||
        currentPayment.data?.authorizationUrl ||
        currentPayment.data?.authorization_url ||
        currentPayment.link ||
        currentPayment.data?.link ||
        currentPayment.url ||
        currentPayment.data?.url;

      if (authorizationUrl) {
        // Redirect to payment gateway
        window.location.href = authorizationUrl;
      } else {
        console.warn(
          "Payment initiated but no authorization URL found:",
          currentPayment,
        );
      }
    }
  }, [currentPayment]);

  const handlePlaceOrder = async () => {
    if (!user) {
      toast.error("Please log in to complete checkout.");
      setIsLoginModalOpen(true);
      return;
    }

    // Validate shipping address
    if (deliveryMethod === "delivery") {
      if (
        !shippingAddress.firstName ||
        !shippingAddress.lastName ||
        !shippingAddress.email ||
        !shippingAddress.phone ||
        !shippingAddress.address ||
        !shippingAddress.city ||
        !shippingAddress.state ||
        !shippingAddress.zipCode ||
        !shippingAddress.country
      ) {
        toast.error("Please fill in all required shipping address fields");
        return;
      }
    } else {
      // Validate contact info for pickup
      if (
        !shippingAddress.firstName ||
        !shippingAddress.lastName ||
        !shippingAddress.email ||
        !shippingAddress.phone
      ) {
        toast.error(
          "Please fill in your contact information (Name, Email, Phone)",
        );
        return;
      }
    }

    // Validate cart
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      navigate("/cart");
      return;
    }

    setPlacingOrder(true);
    try {
      // Get user email (from user or shipping address)
      const email = user?.email || shippingAddress.email;
      if (!email) {
        toast.error("Email is required for payment");
        return;
      }

      // Prepare order data
      const userId = user?.id || user?._id || null;

      // Order items - backend uses database prices, so don't send prices
      // Include variationId if present
      // Note: item.id is the numeric database ID, item.productId is the string SKU
      // Backend expects numeric product_id
      const orderItems = cartItems
        .map((item) => ({
          product_id: item.id, // Use numeric database ID (not string productId/SKU)
          quantity: item.quantity || 1,
          // Include variation_id if present (backend expects variation_id)
          ...(item.variationId && { variation_id: item.variationId }),
          // Don't send price, final_price, basePrice, or discount
          // Backend will use database prices
        }))
        .filter((item) => item.product_id && item.quantity > 0);

      if (orderItems.length === 0) {
        toast.error("No valid items in cart");
        return;
      }

      // Calculate shipping cost (tax is calculated on backend)
      // If shipping cost is not calculated yet, use 0 (will be validated)
      const calculatedShipping = shippingCost !== null ? shippingCost : 0;

      // Validate shipping cost is calculated
      if (shippingCost === null && deliveryMethod === "delivery") {
        toast.error("Please enter your state to calculate shipping cost");
        return;
      }

      // Prepare complete shipping address based on method
      const firstName = shippingAddress.firstName?.trim() || "";
      const lastName = shippingAddress.lastName?.trim() || "";
      const fullName = `${firstName} ${lastName}`.trim();

      let completeShippingAddress;

      if (deliveryMethod === "pickup") {
        completeShippingAddress = {
          recipient_name: fullName,
          address_line1: PICKUP_ADDRESS.address,
          city: PICKUP_ADDRESS.city,
          state: PICKUP_ADDRESS.state,
          country: PICKUP_ADDRESS.country,
          zip_code: PICKUP_ADDRESS.zipCode,
          firstName,
          lastName,
          name: fullName,
          fullName: fullName,
          email: shippingAddress.email?.trim(),
          phone: shippingAddress.phone?.trim(),
          address: PICKUP_ADDRESS.address,
          is_pickup: true,
        };
      } else {
        completeShippingAddress = {
          recipient_name: fullName,
          address_line1: shippingAddress.address?.trim(),
          city: shippingAddress.city?.trim(),
          state: shippingAddress.state?.trim(),
          country: shippingAddress.country?.trim(),
          zip_code: shippingAddress.zipCode?.trim(),
          firstName,
          lastName,
          name: fullName,
          fullName: fullName,
          email: shippingAddress.email?.trim(),
          phone: shippingAddress.phone?.trim(),
          address: shippingAddress.address?.trim(),
        };
      }

      // Validate shipping address fields
      if (
        !completeShippingAddress.recipient_name ||
        !completeShippingAddress.address_line1 ||
        !completeShippingAddress.city ||
        !completeShippingAddress.state ||
        !completeShippingAddress.zip_code ||
        !completeShippingAddress.country
      ) {
        toast.error("Please fill in all required shipping address fields");
        return;
      }

      // Get coupon discount amount if coupon is applied
      const couponDiscountAmount = coupon?.discountAmount || 0;

      // Create order first (with pending payment status)
      // taxAmount is now calculated on backend from system settings
      const orderResult = await dispatch(
        createOrder({
          userId: userId || undefined, // Send undefined instead of null if no user
          items: orderItems,
          shippingCost: parseFloat(calculatedShipping.toFixed(2)) || 0,
          // taxAmount removed - backend calculates it from system settings
          discountAmount: couponDiscountAmount,
          paymentMethod: selectedPaymentMethod,
          paymentStatus: "pending",
          shippingAddress: completeShippingAddress,
          status: "Pending",
          notes: deliveryMethod === "pickup" ? "Pickup Order" : null,
          couponId: coupon?.id || null, // Include coupon ID for tracking
        }),
      ).unwrap();

      // Apply coupon if one was used (mark it as used in the database)
      if (coupon?.id) {
        try {
          await axios.post("/coupons/apply", {
            coupon_id: coupon.id,
            order_id: orderResult.orderId,
          });
        } catch (error) {
          // Log error but don't fail the order - coupon discount was already applied
          console.error("Failed to mark coupon as used:", error);
        }
      }

      // After order is created, initiate payment with orderId in metadata
      // Use backend-calculated total from order response
      const paymentAmount = orderResult.total;

      const paymentResult = await dispatch(
        initiatePayment({
          gateway: selectedPaymentMethod,
          email,
          amount: paymentAmount,
          metadata: {
            orderId: orderResult.orderId,
            userId,
          },
          // Remove callbackUrl - backend has fallback
          // callbackUrl: `${window.location.origin}/payment/callback`,
        }),
      ).unwrap();

      // Check for authorization URL in various possible fields
      const authorizationUrl =
        paymentResult?.authorizationUrl ||
        paymentResult?.authorization_url ||
        paymentResult?.data?.authorizationUrl ||
        paymentResult?.data?.authorization_url ||
        paymentResult?.link ||
        paymentResult?.data?.link ||
        paymentResult?.url ||
        paymentResult?.data?.url;

      if (authorizationUrl) {
        // Redirect immediately to payment gateway
        toast.success("Redirecting to payment gateway...");
        // Don't reset placingOrder here - we're redirecting anyway
        window.location.href = authorizationUrl;
      } else {
        // Fallback: wait for useEffect to handle redirect
        toast.success("Payment initiated. Redirecting...");
        console.warn(
          "No authorization URL found in payment result:",
          paymentResult,
        );
        setPlacingOrder(false);
      }
    } catch (error) {
      // Enhanced error logging for debugging
      console.error("Order creation or payment initiation failed:", {
        error: error,
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack,
      });

      // Error already handled in thunk, but provide additional context
      if (error.response?.status === 400) {
        toast.error(
          "Invalid order data. Please check your information and try again.",
        );
      } else if (error.response?.status === 401) {
        toast.error("Please log in to continue.");
      } else if (error.response?.status >= 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Failed to process order. Please try again.");
      }
      setPlacingOrder(false);
    }
  };

  // Login handler - stays on checkout page
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post("/auth/login", {
        email: loginEmail,
        password: loginPassword,
      });

      setUser(res.data.user);
      toast.success(`Welcome back, ${res.data.user.firstName || "User"}!`);
      // Sync cart to server after login
      await dispatch(syncCartToServer());
      // Reload cart from server
      await dispatch(loadCart(res.data.user));

      // Close modal and stay on checkout page
      setIsLoginModalOpen(false);
      setLoginEmail("");
      setLoginPassword("");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.request?.responseText ||
        err.message ||
        "Login failed";
      toast.error(message);
    } finally {
      setLoginLoading(false);
    }
  };

  // Register handler - stays on checkout page
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      return toast.error("You must accept the Terms and Privacy Policy");
    }

    const { confirmPassword, ...cleanedFormData } = registerFormData;

    if (registerFormData.password !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    setRegisterLoading(true);

    try {
      await registerUser(cleanedFormData);
      toast.success(
        "Registration successful! Please check your email to verify your account.",
      );

      // Close modal and stay on checkout page
      setIsRegisterModalOpen(false);
      setRegisterFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
      });
      setAcceptedTerms(false);
    } catch (error) {
      const res = error?.response;

      if (res?.data?.errors && Array.isArray(res.data.errors)) {
        res.data.errors.forEach((err) => {
          const fieldName = err.field || "Field";
          toast.error(`${fieldName}: ${err.message}`);
        });
      } else {
        toast.error(res?.data?.message || "Registration failed");
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleRegisterChange = (e) => {
    const { id, value } = e.target;
    setRegisterFormData((prev) => ({ ...prev, [id]: value }));
  };

  if (loading || authLoading) {
    return (
      <>
        <Seo
          {...routeSeoConfig.checkout}
          type="website"
          noindex={routeSeoConfig.checkout.noindex}
        />
        <CheckoutSkeleton />
      </>
    );
  }

  // Redirect handled by useEffect above
  if (!user || cartItems.length === 0) {
    return null;
  }

  return (
    <>
      <Seo
        {...routeSeoConfig.checkout}
        type="website"
        noindex={routeSeoConfig.checkout.noindex}
      />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <Link
                to="/cart"
                className="flex items-center gap-2 text-primary hover:text-secondary transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Cart
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-secondary" />
                <span className="text-sm text-gray-600">Secure Checkout</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-center gap-8">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm font-medium text-primary">Cart</span>
              </div>
              <div className="w-12 h-px bg-secondary"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <span className="text-sm font-medium text-primary">
                  Checkout
                </span>
              </div>
              <div className="w-12 h-px bg-gray-200"></div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="text-gray-400 text-sm font-bold">3</span>
                </div>
                <span className="text-sm text-gray-400">Confirmation</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-8">
              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                {/* <h2 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact Information
              </h2> */}

                {/* Account Question - Only show if user is not logged in */}
                {!user || (!user.email && !user.id && !user._id) ? (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      Do you have an account?
                    </p>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsLoginModalOpen(true)}
                        className="flex-1"
                      >
                        Yes, Login
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsRegisterModalOpen(true)}
                        className="flex-1"
                      >
                        No, Register
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">
                      Logged in as:{" "}
                      <span className="font-medium">
                        {user?.email || user?.firstName || "User"}
                      </span>
                    </p>
                  </div>
                )}

                {/* <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    className="mt-1"
                    defaultValue={user?.email || ""}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-4">
                <Checkbox id="newsletter" />
                <Label htmlFor="newsletter" className="text-sm">
                  Subscribe to our newsletter for skincare tips and exclusive
                  offers
                </Label>
              </div> */}
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-primary flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Shipping Address
                  </h2>
                  {user && (user.email || user.id || user._id) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddressSelectorOpen(true)}
                      className="flex items-center gap-2"
                      disabled={deliveryMethod === "pickup"}
                    >
                      <MapPin className="w-4 h-4" />
                      Use Saved Address
                    </Button>
                  )}
                </div>

                {/* Delivery Method Selector */}
                <div className="mb-6">
                  <Label className="text-base font-semibold mb-3 block">
                    Delivery Method
                  </Label>
                  <RadioGroup
                    defaultValue="delivery"
                    value={deliveryMethod}
                    onValueChange={setDeliveryMethod}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <div>
                      <RadioGroupItem
                        value="delivery"
                        id="delivery"
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor="delivery"
                        className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                      >
                        <Truck className="mb-3 h-6 w-6" />
                        <div className="text-center">
                          <span className="block font-semibold">Delivery</span>
                          <span className="text-xs text-gray-500">
                            Ship to your address
                          </span>
                        </div>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem
                        value="pickup"
                        id="pickup"
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor="pickup"
                        className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-transparent p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
                      >
                        <Store className="mb-3 h-6 w-6" />
                        <div className="text-center">
                          <span className="block font-semibold">Pickup</span>
                          <span className="text-xs text-gray-500">
                            Form our Abuja Store
                          </span>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      className="mt-1"
                      value={shippingAddress.firstName}
                      onChange={(e) =>
                        setShippingAddress((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      className="mt-1"
                      value={shippingAddress.lastName}
                      onChange={(e) =>
                        setShippingAddress((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                    />
                  </div>

                  {deliveryMethod === "delivery" ? (
                    <>
                      <div className="md:col-span-2">
                        <Label htmlFor="address">Address *</Label>
                        <Input
                          id="address"
                          placeholder="123 Main Street"
                          className="mt-1"
                          value={shippingAddress.address}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              address: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="shipping-email">Email Address *</Label>
                        <Input
                          id="shipping-email"
                          type="email"
                          placeholder="your@email.com"
                          className="mt-1 bg-gray-50"
                          value={shippingAddress.email}
                          readOnly
                        />
                      </div>
                      <div>
                        <Label htmlFor="shipping-phone">Phone Number *</Label>
                        <Input
                          id="shipping-phone"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          className="mt-1"
                          value={shippingAddress.phone}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              phone: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          placeholder="New York"
                          className="mt-1"
                          value={shippingAddress.city}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          placeholder="NY"
                          className="mt-1"
                          value={shippingAddress.state}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              state: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="zipCode">ZIP Code *</Label>
                        <Input
                          id="zipCode"
                          placeholder="10001"
                          className="mt-1"
                          value={shippingAddress.zipCode}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              zipCode: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label htmlFor="country">Country *</Label>
                        <Input
                          id="country"
                          placeholder="United States"
                          className="mt-1"
                          value={shippingAddress.country}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              country: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <Label htmlFor="shipping-email">Email Address *</Label>
                        <Input
                          id="shipping-email"
                          type="email"
                          placeholder="your@email.com"
                          className="mt-1 bg-gray-50"
                          value={shippingAddress.email}
                          readOnly
                        />
                      </div>
                      <div>
                        <Label htmlFor="shipping-phone">Phone Number *</Label>
                        <Input
                          id="shipping-phone"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          className="mt-1"
                          value={shippingAddress.phone}
                          onChange={(e) =>
                            setShippingAddress((prev) => ({
                              ...prev,
                              phone: e.target.value,
                            }))
                          }
                        />
                      </div>

                      {/* Pickup Address Display */}
                      <div className="md:col-span-2 mt-4">
                        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-primary mt-0.5" />
                            <div>
                              <h4 className="font-semibold text-primary mb-1">
                                Pickup Location
                              </h4>
                              <p className="text-gray-700">
                                {PICKUP_ADDRESS.address}
                              </p>
                              <p className="text-gray-700">
                                {PICKUP_ADDRESS.city}, {PICKUP_ADDRESS.state}
                              </p>
                              <p className="text-sm text-gray-500 mt-2">
                                Open Mon-Sat, 9am - 6pm
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Shipping Method */}
              {deliveryMethod === "delivery" && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-xl font-bold text-primary mb-6">
                    Shipping Method
                  </h2>
                  {shippingAddress.state &&
                  shippingAddress.state.trim() &&
                  shippingCost !== null ? (
                    <RadioGroup defaultValue="standard" className="space-y-4">
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem value="standard" id="standard" />
                          <div>
                            <Label htmlFor="standard" className="font-medium">
                              {(() => {
                                const totalWeight = cartItems.reduce(
                                  (sum, item) => {
                                    const weight = item.weight || 0.5;
                                    return sum + weight * (item.quantity || 1);
                                  },
                                  0,
                                );
                                return getShippingMethodDetails(totalWeight)
                                  .name;
                              })()}
                            </Label>
                            <p className="text-sm text-gray-600">
                              {getEstimatedDelivery(shippingAddress.state)}
                            </p>
                          </div>
                        </div>
                        <span className="font-bold">
                          {formatNaira(shippingCost)}
                        </span>
                      </div>
                    </RadioGroup>
                  ) : (
                    <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                      <p className="text-sm text-yellow-800">
                        Please enter your state above to calculate shipping
                        cost.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Method */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Method
                </h2>

                {gatewayStatusesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="animate-spin h-6 w-6 text-primary" />
                    <span className="ml-2 text-gray-600">
                      Loading payment options...
                    </span>
                  </div>
                ) : !paystackEnabled && !flutterwaveEnabled ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      No payment gateways are currently available. Please
                      contact support.
                    </p>
                  </div>
                ) : (
                  <RadioGroup
                    value={selectedPaymentMethod}
                    onValueChange={handlePaymentMethodChange}
                    className="space-y-4"
                  >
                    {/* Paystack Option */}
                    {paystackEnabled && (
                      <Label
                        htmlFor="paystack"
                        className={`flex items-center justify-between p-4 border rounded-lg transition-colors cursor-pointer ${
                          selectedPaymentMethod === "paystack"
                            ? "border-primary"
                            : "border-gray-200 hover:border-primary"
                        }`}
                        style={{ userSelect: "none" }}
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem
                            value="paystack"
                            id="paystack"
                            className="peer"
                          />
                          <div className="flex items-center gap-3">
                            <img
                              src={PaystackLogo}
                              alt="Paystack"
                              className="w-12 h-8 object-contain rounded bg-white"
                            />
                            <div>
                              <span className="font-medium text-primary">
                                Paystack
                              </span>
                              <p className="text-sm text-gray-600">
                                Pay securely with cards, bank transfer, or USSD
                              </p>
                            </div>
                          </div>
                        </div>
                      </Label>
                    )}

                    {/* Flutterwave Option */}
                    {flutterwaveEnabled && (
                      <Label
                        htmlFor="flutterwave"
                        className={`flex items-center justify-between p-4 border rounded-lg transition-colors cursor-pointer ${
                          selectedPaymentMethod === "flutterwave"
                            ? "border-primary"
                            : "border-gray-200 hover:border-primary"
                        }`}
                        style={{ userSelect: "none" }}
                      >
                        <div className="flex items-center space-x-3">
                          <RadioGroupItem
                            value="flutterwave"
                            id="flutterwave"
                            className="peer"
                          />
                          <div className="flex items-center gap-3">
                            <img
                              src={FlutterwaveLogo}
                              alt="Flutterwave"
                              className="w-12 h-8 object-contain rounded bg-white"
                            />
                            <div>
                              <span className="font-medium text-primary">
                                Flutterwave
                              </span>
                              <p className="text-sm text-gray-600">
                                Pay securely with cards, bank transfer, or USSD
                              </p>
                            </div>
                          </div>
                        </div>
                      </Label>
                    )}
                  </RadioGroup>
                )}

                {/* Payment Method Info */}
                {!gatewayStatusesLoading &&
                  (paystackEnabled || flutterwaveEnabled) && (
                    <div className="mt-6 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-secondary" />
                        <span className="text-sm font-medium text-primary">
                          Secure Payment
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        {selectedPaymentMethod === "paystack"
                          ? "You'll be redirected to Paystack's secure payment page to complete your transaction."
                          : "You'll be redirected to Flutterwave's secure payment page to complete your transaction."}
                      </p>
                    </div>
                  )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
                <h2 className="text-xl font-bold text-primary mb-6">
                  Order Summary
                </h2>

                {/* Order Items */}
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.productId || item.id} className="flex gap-3">
                      <div className="relative">
                        <img
                          src={item.image || "/placeholder.svg"}
                          alt={item.name}
                          width={80}
                          height={80}
                          className="rounded-lg object-cover"
                        />
                        <Badge className="absolute -top-2 -right-2 bg-secondary text-primary text-xs">
                          {item.quantity}
                        </Badge>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-primary text-sm">
                          {item.name}
                        </h4>
                        <p className="text-primary font-bold">
                          {formatNaira(item.finalPrice || item.price || 0)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="my-6" />

                {/* Price Breakdown */}
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Subtotal ({cartItems.length} items)
                    </span>
                    <span className="font-medium">{formatNaira(subtotal)}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium">
                      {shippingCost === null ? (
                        <span className="text-gray-400 text-sm italic">
                          Enter state to calculate
                        </span>
                      ) : shippingCost === 0 ? (
                        <span className="text-green-600">Free</span>
                      ) : (
                        <div className="text-right">
                          <div>{formatNaira(shippingCost)}</div>
                          <div className="text-xs text-gray-500">
                            {getEstimatedDelivery(shippingAddress.state)}
                          </div>
                        </div>
                      )}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">
                      {cartTotalsLoading ? (
                        <span className="text-gray-400">Calculating...</span>
                      ) : (
                        formatNaira(displayTax)
                      )}
                    </span>
                  </div>

                  <Separator />

                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-primary">Total</span>
                    <span className="text-primary">
                      {cartTotalsLoading ? (
                        <span className="text-gray-400">Calculating...</span>
                      ) : (
                        formatNaira(displayTotal)
                      )}
                    </span>
                  </div>
                </div>

                {/* Updated Place Order button */}
                <Button
                  className="w-full mt-6 bg-primary hover:bg-primary/90 text-white py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handlePlaceOrder}
                  disabled={
                    placingOrder ||
                    initiatingPayment ||
                    cartItems.length === 0 ||
                    gatewayStatusesLoading ||
                    (!paystackEnabled && !flutterwaveEnabled) ||
                    shippingCost === null
                  }
                >
                  {placingOrder || initiatingPayment ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="animate-spin h-5 w-5" />
                      Processing...
                    </span>
                  ) : !paystackEnabled && !flutterwaveEnabled ? (
                    "No Payment Method Available"
                  ) : selectedPaymentMethod === "paystack" ? (
                    "Pay with Paystack"
                  ) : (
                    "Pay with Flutterwave"
                  )}
                </Button>

                <div className="mt-6 space-y-3 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Shield className="w-4 h-4 text-secondary" />
                    <span>256-bit SSL encryption</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Your payment information is secure and encrypted
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Address Selector Modal */}
        <AddressSelectorModal
          isOpen={isAddressSelectorOpen}
          onClose={() => setIsAddressSelectorOpen(false)}
          onSelectAddress={handleSelectAddress}
        />

        {/* Login Modal */}
        <Dialog open={isLoginModalOpen} onOpenChange={setIsLoginModalOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-primary">
                Welcome Back
              </DialogTitle>
              <DialogDescription>
                Sign in to your account to continue
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLogin} className="space-y-4 mt-4">
              <div>
                <Label
                  htmlFor="login-email"
                  className="text-primary font-medium"
                >
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="pl-10 py-3 border-gray-200 focus:border-secondary"
                    required
                  />
                </div>
              </div>

              <div>
                <Label
                  htmlFor="login-password"
                  className="text-primary font-medium"
                >
                  Password
                </Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="login-password"
                    type={showLoginPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showLoginPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin h-5 w-5" />
                    Signing In...
                  </span>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Register Modal */}
        <Dialog
          open={isRegisterModalOpen}
          onOpenChange={setIsRegisterModalOpen}
        >
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-primary">
                Create Your Account
              </DialogTitle>
              <DialogDescription>
                Join thousands of satisfied customers
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRegister} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="firstName"
                    className="text-primary font-medium"
                  >
                    First Name
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="firstName"
                      type="text"
                      placeholder="John"
                      className="pl-10 py-3 border-gray-200 focus:border-secondary"
                      value={registerFormData.firstName}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="lastName"
                    className="text-primary font-medium"
                  >
                    Last Name
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="lastName"
                      type="text"
                      placeholder="Doe"
                      className="pl-10 py-3 border-gray-200 focus:border-secondary"
                      value={registerFormData.lastName}
                      onChange={handleRegisterChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-primary font-medium">
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className="pl-10 py-3 border-gray-200 focus:border-secondary"
                    value={registerFormData.email}
                    onChange={handleRegisterChange}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-primary font-medium">
                  Phone Number
                </Label>
                <div className="relative mt-2">
                  <PhoneInput
                    country={"ng"}
                    value={
                      registerFormData.phone
                        ? registerFormData.phone.replace("+", "")
                        : ""
                    }
                    onChange={(phone) =>
                      setRegisterFormData((prev) => ({
                        ...prev,
                        phone: `+${phone}`,
                      }))
                    }
                    inputClass="!pl-14 !pr-4 !py-2 !w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:outline-none text-sm"
                    buttonClass="!bg-transparent !border-none"
                    containerClass="!w-full"
                    inputProps={{
                      name: "phone",
                      id: "phone",
                      required: true,
                    }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="password"
                    className="text-primary font-medium"
                  >
                    Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="password"
                      type={showRegisterPassword ? "text" : "password"}
                      placeholder="Create a password"
                      className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                      value={registerFormData.password}
                      onChange={handleRegisterChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowRegisterPassword(!showRegisterPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showRegisterPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <Label
                    htmlFor="confirmPassword"
                    className="text-primary font-medium"
                  >
                    Confirm Password
                  </Label>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                      value={registerFormData.confirmPassword}
                      onChange={handleRegisterChange}
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  className="mt-1"
                  checked={acceptedTerms}
                  onCheckedChange={(checked) => setAcceptedTerms(!!checked)}
                />
                <Label
                  htmlFor="terms"
                  className="text-sm text-gray-600 leading-relaxed"
                >
                  I agree to the{" "}
                  <Link
                    to="/terms"
                    className="text-secondary hover:text-secondary/80 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy"
                    className="text-secondary hover:text-secondary/80 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={registerLoading || !acceptedTerms}
              >
                {registerLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin h-5 w-5" />
                    Creating Account...
                  </span>
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
